import React, { useState } from 'react';
import { useUserProfileModal } from '../../context/UserProfileModalContext.tsx';
import { User } from '../../../shared/types.ts';

interface AvatarProps {
  src?: string | null;
  name?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isOnline?: boolean;
  className?: string;
  userId?: string;
  userData?: User;
  onClick?: (e: React.MouseEvent) => void;
  disableProfileClick?: boolean;
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  name = 'User',
  size = 'md',
  isOnline,
  className = '',
  userId,
  userData,
  onClick,
  disableProfileClick = false,
}) => {
  const { openUserProfile } = useUserProfileModal();
  const [imageError, setImageError] = useState(false);

  // Safe fallback name that never throws on null, undefined, numbers, or whitespace
  const safeName =
    typeof name === 'string' && name.trim().length > 0 ? name.trim() : 'User';

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick(e);
      return;
    }
    if (!disableProfileClick && (userId || userData || safeName !== 'User')) {
      e.stopPropagation();
      openUserProfile(userData || userId || safeName);
    }
  };

  const sizeMap = {
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-9 h-9 text-sm',
    lg: 'w-11 h-11 text-base',
    xl: 'w-16 h-16 text-xl',
  };

  const statusIndicatorMap = {
    xs: 'w-1.5 h-1.5 bottom-0 right-0',
    sm: 'w-2 h-2 bottom-0 right-0',
    md: 'w-2.5 h-2.5 bottom-0.5 right-0.5',
    lg: 'w-3 h-3 bottom-0.5 right-0.5',
    xl: 'w-4 h-4 bottom-1 right-1',
  };

  const getInitials = (n: string) => {
    try {
      const parts = n.split(/\s+/).filter(Boolean);
      if (parts.length >= 2 && parts[0] && parts[1]) {
        const first = parts[0][0] || '';
        const second = parts[1][0] || '';
        if (first || second) {
          return `${first}${second}`.toUpperCase();
        }
      }
      if (parts.length === 1 && parts[0].length >= 2) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      return n.slice(0, 2).toUpperCase() || 'U';
    } catch {
      return 'U';
    }
  };

  // Deterministic pastel color palette based on name string
  const colors = [
    'bg-blue-500 text-white',
    'bg-indigo-500 text-white',
    'bg-emerald-500 text-white',
    'bg-amber-500 text-white',
    'bg-purple-500 text-white',
    'bg-rose-500 text-white',
    'bg-teal-500 text-white',
    'bg-cyan-500 text-white',
  ];

  let hash = 0;
  for (let i = 0; i < safeName.length; i++) {
    hash = safeName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorClass = colors[Math.abs(hash) % colors.length];

  const showImage = Boolean(src) && !imageError;

  return (
    <div
      onClick={handleClick}
      className={`relative inline-flex flex-shrink-0 cursor-pointer transition-transform hover:scale-105 active:scale-95 ${className}`}
      title={`${safeName} (Click to view profile & chat)`}
    >
      {showImage ? (
        <img
          src={src!}
          alt={safeName}
          className={`${sizeMap[size]} rounded-full object-cover ring-1 ring-surface-200 dark:ring-surface-700`}
          onError={() => setImageError(true)}
        />
      ) : (
        <div
          className={`${sizeMap[size]} rounded-full flex items-center justify-center font-semibold uppercase tracking-wider ${colorClass} shadow-subtle`}
        >
          {getInitials(safeName)}
        </div>
      )}

      {isOnline !== undefined && (
        <span
          className={`absolute rounded-full ring-2 ring-white dark:ring-surface-900 ${statusIndicatorMap[size]} ${
            isOnline ? 'bg-emerald-500' : 'bg-surface-400'
          }`}
        />
      )}
    </div>
  );
};
